import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getRoles, createRole, updateRole } from "../api/users";
import { Role } from "../types/user";
import { useNotification } from "../hooks/useNotification";

const RolesPage: React.FC = () => {
    const { t } = useTranslation(["roles", "common"]);
    const { showNotification } = useNotification();
    const queryClient = useQueryClient();

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [editingRole, setEditingRole] = useState<Role | null>(null);
    const [formData, setFormData] = useState({
        name: "",
        guard_name: "api",
    });

    const {
        data: roles,
        isLoading,
        error,
    } = useQuery({
        queryKey: ["roles"],
        queryFn: getRoles,
    });

    const createRoleMutation = useMutation({
        mutationFn: createRole,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["roles"] });
            showNotification(t("role_created_successfully"), "success");
            setIsCreateModalOpen(false);
            setFormData({ name: "", guard_name: "api" });
        },
        onError: () => {
            showNotification(t("common:error_creating_role"), "error");
        },
    });

    const updateRoleMutation = useMutation({
        mutationFn: ({
            id,
            data,
        }: {
            id: number;
            data: { name?: string; guard_name?: string };
        }) => updateRole(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["roles"] });
            showNotification(t("role_updated_successfully"), "success");
            setEditingRole(null);
            setFormData({ name: "", guard_name: "api" });
        },
        onError: () => {
            showNotification(t("common:error_updating_role"), "error");
        },
    });

    const handleCreateRole = (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.name.trim()) {
            showNotification(t("common:role_name_required"), "error");
            return;
        }
        createRoleMutation.mutate(formData);
    };

    const handleUpdateRole = (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingRole || !formData.name.trim()) {
            showNotification(t("common:role_name_required"), "error");
            return;
        }
        updateRoleMutation.mutate({
            id: editingRole.id,
            data: formData,
        });
    };

    const handleEditRole = (role: Role) => {
        setEditingRole(role);
        setFormData({
            name: role.name,
            guard_name: role.guard_name,
        });
    };

    const handleCancel = () => {
        setIsCreateModalOpen(false);
        setEditingRole(null);
        setFormData({ name: "", guard_name: "api" });
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center py-8">
                <p className="text-red-600">
                    {t("common:error_loading_roles")}
                </p>
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {t("title")}
                </h1>
                <button
                    onClick={() => setIsCreateModalOpen(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    {t("create_new_role")}
                </button>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 dark:bg-gray-900">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {t("id")}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {t("name")}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {t("guard_name")}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {t("created_at")}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {t("common:actions")}
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200">
                        {roles?.map((role) => (
                            <tr
                                key={role.id}
                                className="hover:bg-gray-50 dark:hover:bg-gray-900"
                            >
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                    {role.id}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                    {role.name}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    {role.guard_name}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    {(() => {
                                        const dateValue =
                                            role.created_at ||
                                            (role as any).createdAt ||
                                            (role as any).created_at;
                                        if (dateValue) {
                                            try {
                                                const date = new Date(
                                                    dateValue
                                                );
                                                if (isNaN(date.getTime())) {
                                                    console.error(
                                                        "Invalid date value:",
                                                        dateValue
                                                    );
                                                    return (
                                                        String(dateValue) || "-"
                                                    );
                                                }
                                                return date.toLocaleDateString();
                                            } catch (error) {
                                                console.error(
                                                    "Error parsing date:",
                                                    dateValue,
                                                    error
                                                );
                                                return String(dateValue) || "-";
                                            }
                                        }
                                        return "-";
                                    })()}
                                    {/* Временная отладка: {JSON.stringify(role.created_at)} */}
                                    {/* Все свойства: {JSON.stringify(role)} */}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    <button
                                        onClick={() => handleEditRole(role)}
                                        className="text-blue-600 hover:text-blue-900 transition-colors"
                                    >
                                        {t("common:edit")}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Модальное окно создания роли */}
            {isCreateModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
                        <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                            {t("create_new_role")}
                        </h2>
                        <form onSubmit={handleCreateRole} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t("name")}
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            name: e.target.value,
                                        })
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t("guard_name")}
                                </label>
                                <select
                                    value={formData.guard_name}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            guard_name: e.target.value,
                                        })
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="api">API</option>
                                    <option value="web">Web</option>
                                </select>
                            </div>
                            <div className="flex justify-end space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    className="px-4 py-2 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:bg-gray-900"
                                >
                                    {t("common:cancel")}
                                </button>
                                <button
                                    type="submit"
                                    disabled={createRoleMutation.isPending}
                                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {createRoleMutation.isPending
                                        ? t("common:creating")
                                        : t("common:create")}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Модальное окно редактирования роли */}
            {editingRole && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
                        <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                            {t("edit_role")}
                        </h2>
                        <form onSubmit={handleUpdateRole} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t("name")}
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            name: e.target.value,
                                        })
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t("guard_name")}
                                </label>
                                <select
                                    value={formData.guard_name}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            guard_name: e.target.value,
                                        })
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="api">API</option>
                                    <option value="web">Web</option>
                                </select>
                            </div>
                            <div className="flex justify-end space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    className="px-4 py-2 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:bg-gray-900"
                                >
                                    {t("common:cancel")}
                                </button>
                                <button
                                    type="submit"
                                    disabled={updateRoleMutation.isPending}
                                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {updateRoleMutation.isPending
                                        ? t("common:saving")
                                        : t("common:save")}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RolesPage;
